import { flow } from 'fp-ts/function';
import { find, get, head, isNil, keys, negate, pickBy, values } from 'lodash/fp';
import { createSelector } from 'reselect';

import { getOrEmptyArray, getProp, getStringProp } from '@mews/fp';
import { pricingModeConfiguration } from '@mews/framework';
import { createPropsExtender } from '@mews/framework/redux';

import { ApplicationState } from '../../../../types';
import { WithEnterpriseId } from '../../../../types/selectors/selectorPropTypes';
import { getEntities } from '../../getEntities';
import { Entities } from '../../types';
import { Enterprise, Enterprises } from '../types';

export const getEnterprises = createSelector<ApplicationState, Entities, Enterprises>(getEntities, get('enterprises'));

export const getEnterprise = createSelector<
    ApplicationState,
    WithEnterpriseId,
    string,
    Enterprises,
    Enterprise | undefined
>(getStringProp('enterpriseId'), getEnterprises, get);

export const getEnterprisePricing = createSelector(getEnterprise, get('pricing'));
export const getEnterpriseChainId = createSelector(getEnterprise, get('chainId'));

export const getEnterprisePricingModeKey = createSelector(getEnterprisePricing, (enterprisePricing) =>
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    enterprisePricing ? pricingModeConfiguration[enterprisePricing] : null
);

export const hasEnterpriseData = flow(getEnterprise, negate(isNil));

export const getCityEnterprises = createSelector(
    getProp<string | undefined>()('cityId'),
    getEnterprises,
    (cityId, enterprises) => pickBy({ cityId }, enterprises)
);

const getConfigurationEnterprise = createSelector(
    getStringProp('configurationId'),
    getEnterprises,
    (configurationId, enterprises) => find({ configurationId }, enterprises)
);

const getEnterpriseAddress = createSelector(getEnterprise, get('address'));
export const getEnterpriseCountryCode = createSelector(getEnterpriseAddress, get('countryCode'));

const getConfigurationEnterpriseId = createSelector(getConfigurationEnterprise, get('id'));
export const configurationEnterpriseIdExtender = createPropsExtender('enterpriseId', getConfigurationEnterpriseId);

export const getCityEnterprisesIds = createSelector(getCityEnterprises, keys);

export const getCityEnterpriseTimezone = flow(getCityEnterprises, values, head, get('ianaTimeZoneIdentifier'));

const getFirstEnterprise = flow(getEnterprises, values, head);

export const getFirstEnterpriseId = flow(getFirstEnterprise, get('id'));

export const getFirstEnterpriseCityId = flow(getFirstEnterprise, get('cityId'));

export const getEnterpriseTimezone = flow(getEnterprise, get('ianaTimeZoneIdentifier'));
export const getEnterpriseDefaultRateCurrencyCode = flow(getEnterprise, get('defaultCurrencyCode'));

export const getEnterpriseImageId = flow(getEnterprise, get('imageId'));
export const getEnterpriseIntroImageId = flow(getEnterprise, get('introImageId'));

export const getEnterpriseAdditionalLegalStatements = flow(getEnterprise, getOrEmptyArray('additionalLegalStatements'));
export const getEnterpriseCityId = flow(getEnterprise, get('cityId'));
export const getEnterpriseEmail = flow(getEnterprise, get('email'));
export const getEnterpriseTelephone = flow(getEnterprise, get('telephone'));
export const getEnterpriseSmsConsent = flow(getEnterprise, get('showSmsConsent'));
